import React, { useContext, useState, useEffect, Suspense, lazy } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTachometerAlt,
  faFile,
  faUsers,
  faCog,
  faChartBar,
  faTools,
  faCheck,
  faBars,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import UserContext from "../../contexts/UserContext";

// Lazy load admin components for better performance
const AdminDashboard = lazy(() => import("./AdminDashboard"));
const AdminPosts = lazy(() => import("./Posts"));
const ReviewBlog = lazy(() => import("./ReviewBlogs"));
const Users = lazy(() => import("./Users"));
const AdminSettings = lazy(() => import("./AdminSettings"));
const AdminReports = lazy(() => import("./AdminReports"));
const AdminTools = lazy(() => import("./AdminTools"));
const SubItem1 = lazy(() => import("./SubItem1"));
const SubItem2 = lazy(() => import("./SubItem2"));

const AdminLayout = () => {
  const { user } = useContext(UserContext);
  const location = useLocation();
  const [open, setOpen] = useState({});
  const [activeComponent, setActiveComponent] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Function to determine if the user is an admin
  const isAdminUser = () => {
    return user && user.role === "admin";
  };

  // Effect to set active component based on the current path
  useEffect(() => {
    const path = location.pathname;
    switch (path) {
      case "/admin/dashboard":
        setActiveComponent("dashboard");
        break;
      case "/admin/posts":
        setActiveComponent("posts");
        break;
      case "/admin/reviewblog":
        setActiveComponent("reviewblog");
        break;
      case "/admin/users":
        setActiveComponent("users");
        break;
      case "/admin/settings":
        setActiveComponent("settings");
        break;
      case "/admin/reports":
        setActiveComponent("reports");
        break;
      case "/admin/tools":
        setActiveComponent("tools");
        break;
      case "/admin/subitem1":
        setActiveComponent("subitem1");
        break;
      case "/admin/subitem2":
        setActiveComponent("subitem2");
        break;
      default:
        setActiveComponent(null);
    }
  }, [location.pathname]);

  // Function to render the active component based on activeComponent state
  const renderActiveComponent = () => {
    switch (activeComponent) {
      case "dashboard":
        return <AdminDashboard />;
      case "posts":
        return <AdminPosts user={user} />;
      case "reviewblog":
        return <ReviewBlog user={user} />;
      case "users":
        return <Users />;
      case "settings":
        return <AdminSettings />;
      case "reports":
        return <AdminReports />;
      case "tools":
        return <AdminTools />;
      case "subitem1":
        return <SubItem1 />;
      case "subitem2":
        return <SubItem2 />;
      default:
        return <div>Select an option from the menu</div>;
    }
  };

  // Handle toggle for submenus
  const handleToggle = (menu) => {
    setOpen((prevOpen) => ({
      ...prevOpen,
      [menu]: !prevOpen[menu],
    }));
  };

  return (
    <div className="flex bg-gray-100 overflow-hidden">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-gray-900 to-gray-800 text-white shadow-xl transform ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 lg:static lg:inset-0 transition-transform duration-300 ease-in-out flex flex-col`}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700 flex-shrink-0">
          <h1 className="text-xl font-bold text-white">Admin Panel</h1>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-2 overflow-y-auto">
          <div className="space-y-1">
            {isAdminUser() ? (
              <>
                {/* Dashboard */}
                <div
                  className={`rounded-lg transition-all duration-200 ${
                    activeComponent === "dashboard"
                      ? "bg-blue-600 shadow-lg"
                      : "hover:bg-gray-700"
                  }`}
                >
                  <Link
                    to="/admin/dashboard"
                    onClick={() => {
                      setActiveComponent("dashboard");
                      setSidebarOpen(false);
                    }}
                    className="flex items-center px-3 py-2 space-x-3 rounded-lg"
                  >
                    <div className="flex items-center justify-center w-8 h-8 bg-white/10 rounded-md">
                      <FontAwesomeIcon
                        icon={faTachometerAlt}
                        className="text-xs"
                      />
                    </div>
                    <span className="text-sm font-medium">Dashboard</span>
                  </Link>
                </div>
                {/* Posts */}
                <div
                  className={`rounded-lg transition-all duration-200 ${
                    activeComponent === "posts"
                      ? "bg-blue-600 shadow-lg"
                      : "hover:bg-gray-700"
                  }`}
                >
                  <Link
                    to="/admin/posts"
                    onClick={() => {
                      setActiveComponent("posts");
                      setSidebarOpen(false);
                    }}
                    className="flex items-center px-3 py-2 space-x-3 rounded-lg"
                  >
                    <div className="flex items-center justify-center w-8 h-8 bg-white/10 rounded-md">
                      <FontAwesomeIcon icon={faFile} className="text-xs" />
                    </div>
                    <span className="text-sm font-medium">Posts</span>
                  </Link>
                </div>
                {/* Review Blog */}
                {user.role === "admin" && (
                  <div
                    className={`rounded-lg transition-all duration-200 ${
                      activeComponent === "reviewblog"
                        ? "bg-blue-600 shadow-lg"
                        : "hover:bg-gray-700"
                    }`}
                  >
                    <Link
                      to="/admin/reviewblog"
                      onClick={() => {
                        setActiveComponent("reviewblog");
                        setSidebarOpen(false);
                      }}
                      className="flex items-center px-3 py-2 space-x-3 rounded-lg"
                    >
                      <div className="flex items-center justify-center w-8 h-8 bg-white/10 rounded-md">
                        <FontAwesomeIcon icon={faCheck} className="text-xs" />
                      </div>
                      <span className="text-sm font-medium">Review Blog</span>
                    </Link>
                  </div>
                )}{" "}
                {/* Users */}
                <div
                  className={`rounded-lg transition-all duration-200 ${
                    activeComponent === "users"
                      ? "bg-blue-600 shadow-lg"
                      : "hover:bg-gray-700"
                  }`}
                >
                  <Link
                    to="/admin/users"
                    onClick={() => {
                      setActiveComponent("users");
                      setSidebarOpen(false);
                    }}
                    className="flex items-center px-3 py-2 space-x-3 rounded-lg"
                  >
                    <div className="flex items-center justify-center w-8 h-8 bg-white/10 rounded-md">
                      <FontAwesomeIcon icon={faUsers} className="text-xs" />
                    </div>
                    <span className="text-sm font-medium">Users</span>
                  </Link>
                </div>{" "}
                {/* Settings */}
                <div
                  className={`rounded-lg transition-all duration-200 ${
                    activeComponent === "settings"
                      ? "bg-blue-600 shadow-lg"
                      : "hover:bg-gray-700"
                  }`}
                >
                  <Link
                    to="/admin/settings"
                    onClick={() => {
                      setActiveComponent("settings");
                      setSidebarOpen(false);
                    }}
                    className="flex items-center px-3 py-2 space-x-3 rounded-lg"
                  >
                    <div className="flex items-center justify-center w-8 h-8 bg-white/10 rounded-md">
                      <FontAwesomeIcon icon={faCog} className="text-xs" />
                    </div>
                    <span className="text-sm font-medium">Settings</span>
                  </Link>
                </div>{" "}
                {/* Reports */}
                <div
                  className={`rounded-lg transition-all duration-200 ${
                    activeComponent === "reports"
                      ? "bg-blue-600 shadow-lg"
                      : "hover:bg-gray-700"
                  }`}
                >
                  <Link
                    to="/admin/reports"
                    onClick={() => {
                      setActiveComponent("reports");
                      setSidebarOpen(false);
                    }}
                    className="flex items-center px-3 py-2 space-x-3 rounded-lg"
                  >
                    <div className="flex items-center justify-center w-8 h-8 bg-white/10 rounded-md">
                      <FontAwesomeIcon icon={faChartBar} className="text-xs" />
                    </div>
                    <span className="text-sm font-medium">Reports</span>
                  </Link>
                </div>
                {/* Tools */}
                <div
                  className={`rounded-lg transition-all duration-200 ${
                    activeComponent === "tools"
                      ? "bg-blue-600 shadow-lg"
                      : "hover:bg-gray-700"
                  }`}
                >
                  <Link
                    to="/admin/tools"
                    onClick={() => {
                      setActiveComponent("tools");
                      setSidebarOpen(false);
                    }}
                    className="flex items-center px-3 py-2 space-x-3 rounded-lg"
                  >
                    <div className="flex items-center justify-center w-8 h-8 bg-white/10 rounded-md">
                      <FontAwesomeIcon icon={faTools} className="text-xs" />
                    </div>
                    <span className="text-sm font-medium">Tools</span>
                  </Link>
                </div>
                {/* Divider */}
                <div className="border-t border-gray-700 my-4"></div>
                {/* Nested Menu */}
                <div className="space-y-2">
                  <button
                    onClick={() => handleToggle("nested1")}
                    className={`w-full flex items-center justify-between p-4 space-x-3 rounded-xl transition-all duration-200 ${
                      open["nested1"] ? "bg-gray-700" : "hover:bg-gray-700"
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-white/10 rounded-lg">
                        <FontAwesomeIcon icon={faTools} className="text-sm" />
                      </div>
                      <span className="font-medium">Nested Menu</span>
                    </div>
                    <div
                      className={`transform transition-transform duration-200 ${
                        open["nested1"] ? "rotate-90" : ""
                      }`}
                    >
                      ▶
                    </div>
                  </button>

                  {open["nested1"] && (
                    <div className="ml-4 space-y-1">
                      <div
                        className={`rounded-lg transition-all duration-200 ${
                          activeComponent === "subitem1"
                            ? "bg-blue-600 shadow-lg"
                            : "hover:bg-gray-700"
                        }`}
                      >
                        <Link
                          to="/admin/subitem1"
                          onClick={() => {
                            setActiveComponent("subitem1");
                            setSidebarOpen(false);
                          }}
                          className="flex items-center px-3 py-2 space-x-3 rounded-lg"
                        >
                          <div className="w-2 h-2 bg-white/40 rounded-full"></div>
                          <span className="text-sm font-medium">Subitem 1</span>
                        </Link>
                      </div>

                      <div
                        className={`rounded-lg transition-all duration-200 ${
                          activeComponent === "subitem2"
                            ? "bg-blue-600 shadow-lg"
                            : "hover:bg-gray-700"
                        }`}
                      >
                        <Link
                          to="/admin/subitem2"
                          onClick={() => {
                            setActiveComponent("subitem2");
                            setSidebarOpen(false);
                          }}
                          className="flex items-center px-3 py-2 space-x-3 rounded-lg"
                        >
                          <div className="w-2 h-2 bg-white/40 rounded-full"></div>
                          <span className="text-sm font-medium">Subitem 2</span>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center p-6">
                <div className="bg-red-100 border border-red-200 rounded-xl p-4">
                  <p className="text-red-800 font-medium">Access Denied</p>
                  <p className="text-red-600 text-sm mt-1">
                    Admin privileges required
                  </p>
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-gray-700 flex-shrink-0">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
              <FontAwesomeIcon icon={faUsers} className="text-white text-xs" />
            </div>
            <div>
              <p className="text-sm font-medium">
                {user?.name || "Admin User"}
              </p>
              <p className="text-xs text-gray-400">
                {user?.role || "Administrator"}
              </p>
            </div>
          </div>
          <div className="text-center text-xs text-gray-400">
            © 2025 Admin Panel
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <FontAwesomeIcon icon={faBars} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {activeComponent
                  ? activeComponent.charAt(0).toUpperCase() +
                    activeComponent.slice(1)
                  : "Admin Panel"}
              </h1>
              <p className="text-sm text-gray-600">
                Manage your platform efficiently
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="hidden md:flex items-center space-x-2 text-sm text-gray-600">
              <span>Welcome back,</span>
              <span className="font-medium text-gray-900">
                {user?.name || "Admin"}
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 bg-gray-50 overflow-y-auto">
          <div className="p-6">
            <Suspense
              fallback={
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <p className="mt-4 text-gray-600 font-medium">Loading...</p>
                  </div>
                </div>
              }
            >
              {renderActiveComponent()}
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
